import { useState } from "react";

/**
 * Simulated progress. Uploads go out through fetch(), which reports no
 * progress events, so a bar eases toward a ceiling and only reaches 100% when
 * the real work has resolved — it never claims a finished operation.
 *
 * A controller lives outside React (the submit loops drive several at once
 * from plain async code); <ProgressBar> subscribes to one.
 */
export function createProgress() {
  let state = { visible: false, value: 0, label: "", failed: false };
  let timer = null;
  const listeners = new Set();
  const set = (patch) => {
    state = { ...state, ...patch };
    listeners.forEach((listener) => listener());
  };
  const stop = () => {
    if (timer) clearInterval(timer);
    timer = null;
  };
  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getSnapshot: () => state,
    start(label, ceiling = 92) {
      stop();
      set({ visible: true, failed: false, label, value: Math.max(state.value, 4) });
      // Ease toward the ceiling: fast at first, crawling near the end.
      timer = setInterval(() => {
        const gap = ceiling - state.value;
        if (gap <= 0.4) return;
        set({ value: state.value + Math.max(gap * 0.08, 0.15) });
      }, 160);
    },
    setLabel: (label) => set({ label }),
    finish(label) {
      stop();
      set({ value: 100, label });
    },
    fail(label) {
      stop();
      set({ failed: true, label });
    },
    reset() {
      stop();
      set({ visible: false, value: 0, label: "", failed: false });
    },
  };
}

/** One progress controller for the component's lifetime. */
export function useProgress() {
  return useState(createProgress)[0];
}

/** A stable map of controllers, made on first use: one per file slot, keyed by the caller. */
export function useProgressMap() {
  const [map] = useState(() => new Map());
  return (key) => {
    if (!map.has(key)) map.set(key, createProgress());
    return map.get(key);
  };
}
